export type ToastType = 'info' | 'success' | 'warn' | 'error';

interface ToastOptions {
  durationMs?: number;
  actionText?: string;
  onAction?: () => void;
}

class ToastManager {
  private activeToast: HTMLElement | null = null;
  private removeTimer: number | null = null;

  public show(message: string, type: ToastType = 'info', options: ToastOptions = {}): void {
    const duration = options.durationMs ?? 3200;

    // Remove existing toast immediately
    this.dismiss();

    const toast = document.createElement('div');
    toast.className = `vyko-toast vyko-toast-${type} animate-slide-down`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');

    const iconSvg = this.getIconSvg(type);

    toast.innerHTML = `
      <div class="vyko-toast-inner">
        <span class="vyko-toast-icon">${iconSvg}</span>
        <span class="vyko-toast-message">${message}</span>
        ${
          options.actionText
            ? `<button class="vyko-toast-action" type="button">${options.actionText}</button>`
            : ''
        }
        <button class="vyko-toast-dismiss" type="button" aria-label="Dismiss">✕</button>
      </div>
    `;

    document.body.appendChild(toast);
    this.activeToast = toast;

    // Attach listeners
    toast.querySelector('.vyko-toast-dismiss')?.addEventListener('click', () => {
      this.dismiss();
    });

    if (options.actionText && options.onAction) {
      toast.querySelector('.vyko-toast-action')?.addEventListener('click', () => {
        options.onAction?.();
        this.dismiss();
      });
    }

    if (duration > 0) {
      this.removeTimer = window.setTimeout(() => {
        this.dismiss();
      }, duration);
    }
  }

  public dismiss(): void {
    if (this.removeTimer !== null) {
      clearTimeout(this.removeTimer);
      this.removeTimer = null;
    }

    if (!this.activeToast) return;

    const el = this.activeToast;
    this.activeToast = null;

    el.classList.remove('animate-slide-down');
    el.classList.add('vyko-toast-exiting');

    setTimeout(() => {
      el.remove();
    }, 220);
  }

  private getIconSvg(type: ToastType): string {
    switch (type) {
      case 'success':
        return `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        `;
      case 'error':
        return `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        `;
      case 'warn':
        return `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        `;
      case 'info':
      default:
        return `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        `;
    }
  }
}

export const toastService = new ToastManager();

export function showToast(message: string, type: ToastType = 'info', options?: ToastOptions): void {
  toastService.show(message, type, options);
}
